import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Search, Menu, X, Mail } from "lucide-react";
import { Logo } from "./Logo";
import { SearchDialog } from "../common/SearchDialog";
import { cx } from "../../lib/format";

export const NAV = [
  { label: "Home", to: "/", end: true },
  { label: "Smart Shopping", to: "/smart-shopping/" },
  { label: "Home", to: "/home/", testId: "household" },
  { label: "Food", to: "/food/" },
  { label: "Travel", to: "/travel/" },
  { label: "Tech", to: "/tech/" },
  { label: "Everyday Tips", to: "/everyday-tips/" },
  { label: "Best Time to Buy", to: "/best-time-to-buy/" },
  { label: "Guides", to: "/guides/" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen(true); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <header className={cx("sticky top-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-300", scrolled ? "border-ab-border bg-background/85 shadow-[0_1px_0_0_rgba(0,0,0,0.02)] backdrop-blur-xl" : "border-transparent bg-ab-bg")} data-testid="site-header">
      <div className="container-ab flex h-16 items-center justify-between gap-4 lg:h-[72px]">
        <div className="flex items-center gap-3 lg:hidden">
          <button type="button" aria-label="Open menu" aria-expanded={open} onClick={() => setOpen(true)} className="-ml-2 rounded-full p-2 text-ab-text hover:bg-ab-soft" data-testid="mobile-menu-button">
            <Menu size={22} />
          </button>
        </div>
        <Logo />
        <nav aria-label="Primary" className="hidden lg:block">
          <ul className="flex items-center gap-1">
            {NAV.map((n) => (
              <li key={n.to}>
                <NavLink to={n.to} end={n.end} data-testid={`nav-link-${n.testId || n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) => cx("whitespace-nowrap rounded-full px-2.5 py-2 text-[13px] font-medium transition-colors xl:px-3 xl:text-[13.5px]", isActive ? "bg-ab-accent-soft text-ab-accent-hover" : "text-ab-text-2 hover:bg-ab-soft hover:text-ab-text")}>
                  {n.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="flex items-center gap-1.5">
          <button type="button" onClick={() => setSearchOpen(true)} aria-label="Search articles" className="rounded-full p-2 text-ab-text transition-colors hover:bg-ab-soft" data-testid="header-search-button">
            <Search size={20} />
          </button>
          <Link to="/#newsletter" className="btn-primary hidden whitespace-nowrap !px-4 !py-2 text-[13px] sm:inline-flex" data-testid="header-newsletter-button">
            <Mail size={15} /> Get Weekly Tips
          </Link>
        </div>
      </div>

      <div className={cx("fixed inset-0 z-[60] lg:hidden", open ? "pointer-events-auto" : "pointer-events-none")} aria-hidden={!open}>
        <div className={cx("absolute inset-0 bg-black/30 transition-opacity duration-300", open ? "opacity-100" : "opacity-0")} onClick={() => setOpen(false)} />
        <div className={cx("absolute left-0 top-0 h-full w-[86%] max-w-sm bg-ab-bg p-6 shadow-2xl transition-transform duration-300 ease-out", open ? "translate-x-0" : "-translate-x-full")} data-testid="mobile-menu-drawer">
          <div className="flex items-center justify-between">
            <Logo />
            <button type="button" aria-label="Close menu" onClick={() => setOpen(false)} className="rounded-full p-2 hover:bg-ab-soft" data-testid="mobile-menu-close-button"><X size={22} /></button>
          </div>
          <nav aria-label="Mobile" className="mt-8">
            <ul className="space-y-1">
              {NAV.map((n) => (
                <li key={n.to}>
                  <NavLink to={n.to} end={n.end} onClick={() => setOpen(false)} className={({ isActive }) => cx("block rounded-xl px-4 py-3 font-serif text-xl", isActive ? "bg-ab-accent-soft text-ab-accent-hover" : "text-ab-text hover:bg-ab-soft")}>{n.label}</NavLink>
                </li>
              ))}
              <li><Link to="/tools/everyday-spending-checker/" onClick={() => setOpen(false)} className="block rounded-xl px-4 py-3 font-serif text-xl text-ab-text hover:bg-ab-soft">Spending Checker</Link></li>
            </ul>
          </nav>
          <Link to="/#newsletter" onClick={() => setOpen(false)} className="btn-primary mt-8 w-full"><Mail size={16} /> Get Weekly Tips</Link>
        </div>
      </div>

      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};
